import { useCallback } from "react";
import { CameraGrid } from "../components/CameraGrid";
import { MapPanel } from "../components/MapPanel";
import { useDevices } from "../hooks/useDevices";
import { useLiveGps } from "../hooks/useLiveGps";
import { usePageRefresh } from "../lib/page-lifecycle";
import type { Device } from "../api";

export function LiveTripPage({
  go,
  deviceId,
  onOpenPlayback,
}: {
  go: (to: string, opts?: { replace?: boolean }) => void;
  deviceId: string | null;
  onOpenPlayback: (device: Device) => void;
}) {
  const { devices, loading, error, refresh } = useDevices();
  usePageRefresh(useCallback(() => refresh(), [refresh]));
  const { positions, connected } = useLiveGps();
  const selected = devices.find((row) => row.id === deviceId) ?? null;
  const name = selected ? selected.label || selected.serial_no : "Vehicle";

  const tripUrl = (id: string, replace = false) => {
    const params = new URLSearchParams(window.location.search);
    params.set("device", id);
    go(`/live-trip?${params.toString()}`, replace ? { replace: true } : undefined);
  };

  const close = () => {
    const from = new URLSearchParams(window.location.search).get("from");
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    go(from === "cameras" ? "/cameras" : "/");
  };

  return (
    <div className="live-trip-page">
      <div className="live-trip-toolbar">
        <button type="button" className="ghost live-trip-back" onClick={close}>
          Back
        </button>
        <div className="live-trip-title">
          <h2>Live trip</h2>
          <p>{selected ? `Following ${name}` : "Select a vehicle"}</p>
        </div>
        <label>
          Vehicle
          <select
            value={selected?.id ?? ""}
            onChange={(e) => {
              if (e.target.value) tripUrl(e.target.value, true);
            }}
            aria-label="Switch live trip vehicle"
          >
            <option value="">Select a vehicle</option>
            {devices.map((row) => (
              <option key={row.id} value={row.id}>
                {(row.label || row.serial_no) + (row.online ? "" : " (offline)")}
              </option>
            ))}
          </select>
        </label>
        <span className={`ws ${connected ? "on" : "off"}`}>
          {connected ? "GPS feed connected" : "GPS feed reconnecting..."}
        </span>
      </div>

      {error ? <div className="banner">{error}</div> : null}
      {loading && devices.length === 0 ? <div className="banner">Loading cameras…</div> : null}

      {selected ? (
        <>
          <section className="live-trip-video" aria-label="Live camera">
            <CameraGrid
              devices={[selected]}
              selectedId={selected.id}
              onSelect={(id) => {
                if (id !== selected.id) tripUrl(id, true);
              }}
              gps={positions}
              gpsSocketConnected={connected}
              liveTripId={selected.id}
              onToggleLiveTrip={() => close()}
              onPlayback={onOpenPlayback}
              viewerScope="trip"
            />
          </section>
          <section className="live-trip-map" aria-label="Live trip map">
            <MapPanel
              devices={devices}
              live={positions}
              selectedId={selected.id}
              onSelect={(id) => tripUrl(id, true)}
              gpsSocketConnected={connected}
              liveTripId={selected.id}
            />
          </section>
        </>
      ) : (
        <p className="empty-grid">This vehicle is not available. Go back and choose a camera.</p>
      )}
    </div>
  );
}
